import { LudsModal, ModalEventHandler } from '../design-system/modal/modal';

export interface ModalOptions extends Partial<LudsModal> {
  eventHandlers?: ModalEventHandler[];
  onClose?: () => void;
  onOpen?: () => void;
}

export class ModalService {
  private static instance: ModalService;
  private activeModals: Map<string, LudsModal> = new Map();
  private modalCounter = 0;
  /**
   * Opens a modal - either creates a new one with content or opens an existing one by ID
   * @param contentOrId - HTML content for new modal OR ID of existing modal
   * @param options - Modal configuration options
   * @returns Promise that resolves with the modal instance
   */
  async openModal(contentOrId: string, options: ModalOptions = {}): Promise<LudsModal> {
    // Check if it's an existing modal by ID
    const existingModal = document.getElementById(contentOrId) as LudsModal;

    if (existingModal && existingModal.tagName?.toLowerCase() === 'luds-modal') {
      // Handle existing modal
      return this.handleExistingModal(existingModal, contentOrId, options);
    }

    // Create new modal with content
    return this.createNewModal(contentOrId, options);
  }

  private async handleExistingModal(modal: LudsModal, modalId: string, options: ModalOptions): Promise<LudsModal> {
    const { eventHandlers, onClose, onOpen, ...modalProps } = options;

    // Update modal properties if provided
    if (Object.keys(modalProps).length > 0) {
      Object.assign(modal, modalProps);
    }

    // Set event handlers if provided
    if (eventHandlers) {
      modal.eventHandlers = eventHandlers;
    }

    // Add custom event listeners
    if (onOpen) {
      modal.addEventListener('luds-dialog-show', onOpen, { once: true });
    }

    if (onClose) {
      modal.addEventListener('luds-dialog-close', onClose, { once: true });
    }

    // Track the modal
    this.activeModals.set(modalId, modal);

    // Open the modal
    modal.open = true;

    // Don't use showModal() for existing modals as it might interfere with page layout
    // Just rely on the open property

    return modal;
  }

  private async createNewModal(content: string, options: ModalOptions): Promise<LudsModal> {
    // Ensure modal component is loaded
    await import('../design-system/modal/modal');

    const modalId = `modal-${++this.modalCounter}`;
    const modal = document.createElement('luds-modal') as LudsModal;

    // Add positioning class
    modal.className = 'luds-modal-fixed';
    modal.setAttribute('data-modal-id', modalId);

    // Extract event handlers and callbacks from options
    const { eventHandlers, onClose, onOpen, ...modalProps } = options;

    // Set modal properties
    Object.assign(modal, modalProps);

    // Set event handlers if provided
    if (eventHandlers) {
      modal.eventHandlers = eventHandlers;
    }

    // Parse and add content
    this.addContent(modal, content);

    // Add custom event listeners
    if (onOpen) {
      modal.addEventListener('luds-dialog-show', onOpen, { once: true });
    }

    // Handle close event for THIS modal specifically
    modal.addEventListener(
      'luds-dialog-close',
      () => {
        if (onClose) {
          onClose();
        }
        // Remove this dynamically created modal from DOM
        this.removeModal(modal);
      },
      { once: true }
    );

    // Track modal
    this.activeModals.set(modalId, modal);

    // Add to DOM and open
    document.body.appendChild(modal);
    modal.open = true;

    // Optional: show with animation
    if (modalProps.animation !== false) {
      modal.show();
    }

    return modal;
  }

  /**
   * Closes a specific modal
   * @param modalIdOrElement - Modal ID or the modal element itself
   */
  closeModal(modalIdOrElement: string | LudsModal): void {
    let modal: LudsModal | undefined;

    if (typeof modalIdOrElement === 'string') {
      modal = this.activeModals.get(modalIdOrElement) || (document.getElementById(modalIdOrElement) as LudsModal);
    } else {
      modal = modalIdOrElement;
    }

    if (modal) {
      modal.close();
    }
  }

  /**
   * Closes all active modals
   */
  closeAllModals(): void {
    this.activeModals.forEach((modal) => {
      modal.close();
    });
  }

  /**
   * Gets an active modal by ID
   * @param modalId - The modal ID
   */
  getModal(modalId: string): LudsModal | undefined {
    return this.activeModals.get(modalId);
  }

  /**
   * Checks if a modal is currently open
   * @param modalId - The modal ID
   */
  isModalOpen(modalId: string): boolean {
    const modal = this.activeModals.get(modalId);
    return modal ? modal.open : false;
  }

  private addContent(modal: LudsModal, content: string): void {
    // Create a temporary container to parse the content
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = content;

    // Move all children to the modal
    while (tempContainer.firstChild) {
      modal.appendChild(tempContainer.firstChild);
    }
  }

  private removeModal(modal: LudsModal): void {
    const modalId = modal.getAttribute('data-modal-id');

    if (modalId) {
      this.activeModals.delete(modalId);
    }

    // Remove from DOM (only called for dynamically created modals)
    if (modal.parentNode) {
      modal.parentNode.removeChild(modal);
    }
  }

  public static getInstance(): ModalService {
    if (!ModalService.instance) {
      ModalService.instance = new ModalService();
    }
    return ModalService.instance;
  }
}
